import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./styles";

const buildPin = (emoji, color) => L.divIcon({
  className: "dp-pin",
  html: `<div style="width:36px;height:36px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 6px 14px rgba(0,0,0,0.25);border:2.5px solid white;"><span style="transform:rotate(45deg);font-size:17px;line-height:1">${emoji}</span></div>`,
  iconSize: [36, 36], iconAnchor: [18, 36],
});
const shopPin = buildPin("🏪", "#8B1A4A");
const homePin = buildPin("🏠", "#0F3D2E");
const mePin = buildPin("🛵", "#C97B3C");

function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 1) return;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
  }, [points, map]);
  return null;
}

const ACTIVE = ["confirming_stock", "picked_up"];
const fmtDist = (km) => km == null ? "" : (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);

export default function DeliveryPartner({ onClose, showToast }) {
  const [online, setOnline] = useState(false);
  const [openOrders, setOpenOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [agentPos, setAgentPos] = useState(null);
  const [posError, setPosError] = useState("");
  const [busy, setBusy] = useState(false);

  const onlineRef = useRef(false);
  const posRef = useRef(null);
  const lastPushRef = useRef(0);
  useEffect(() => { onlineRef.current = online; }, [online]);
  useEffect(() => { posRef.current = agentPos; }, [agentPos]);

  const active = myOrders.find((o) => ACTIVE.includes(o.status));

  // Watch the partner's live GPS.
  useEffect(() => {
    if (!navigator.geolocation) { setPosError("GPS not available on this device"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setAgentPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setPosError(""); },
      (e) => setPosError(e?.message || "Location permission blocked"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    return () => { try { navigator.geolocation.clearWatch(id); } catch (_) { /* ignore */ } };
  }, []);

  const fetchData = useCallback(async () => {
    try { const { data } = await api.get("/delivery/mine"); setMyOrders(data || []); } catch (_) { /* ignore */ }
    if (onlineRef.current) {
      try {
        const p = posRef.current;
        const q = p ? `?lat=${p.lat}&lng=${p.lng}` : "";
        const { data } = await api.get(`/delivery/open-orders${q}`);
        setOpenOrders(data || []);
      } catch (_) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 12000);
    return () => clearInterval(t);
  }, [fetchData]);

  // Push live location for the active delivery (throttled ~10s).
  useEffect(() => {
    if (!active || !agentPos) return;
    const now = Date.now();
    if (now - lastPushRef.current < 9000) return;
    lastPushRef.current = now;
    api.post(`/delivery/orders/${active.id}/location`, { lat: agentPos.lat, lng: agentPos.lng }).catch(() => {});
  }, [agentPos, active]);

  const goOnline = () => { setOnline(true); setTimeout(fetchData, 50); };

  const accept = async (o) => {
    setBusy(true);
    setOpenOrders((prev) => prev.filter((x) => x.id !== o.id)); // optimistic removal
    try {
      await api.post(`/delivery/orders/${o.id}/accept`);
      showToast?.("Order accepted — head to the shop 🛵");
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not accept this order");
    } finally { setBusy(false); fetchData(); }
  };

  const advance = async (o, status) => {
    setBusy(true);
    try {
      await api.post(`/delivery/orders/${o.id}/status`, { status });
      showToast?.(status === "delivered" ? "Delivered! Great job 🎉" : "Marked as picked up ✅");
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not update status");
    } finally { setBusy(false); fetchData(); }
  };

  const completed = myOrders.filter((o) => o.status === "delivered").length;

  return (
    <div data-testid="delivery-partner-modal" style={{ position: "fixed", inset: 0, zIndex: 1150, background: "#FAF9F6", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ flexShrink: 0, background: "#0F3D2E", color: "white", padding: "16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="delivery-close-btn" onClick={onClose} aria-label="Close"
          style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.16)", color: "white", fontSize: 20, cursor: "pointer" }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ ...S.fraunces, fontSize: 20, fontWeight: 700, lineHeight: 1 }}>Delivery Partner</div>
          <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 3 }}>Deliver nearby orders · {completed} completed</div>
        </div>
        <span data-testid="delivery-online-dot" style={{ fontSize: 11, fontWeight: 800, padding: "5px 11px", borderRadius: 100, background: (online || active) ? "#25D366" : "rgba(255,255,255,0.18)" }}>
          {(online || active) ? "● Online" : "○ Offline"}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {posError && (
          <div data-testid="delivery-gps-warning" style={{ background: "#FFF3E0", border: "1px solid #F3CC8B", color: "#8C5A1C", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderRadius: 12, marginBottom: 14 }}>
            📍 {posError}. Enable location so customers can see you on the map.
          </div>
        )}

        {/* ACTIVE DELIVERY */}
        {active && (
          <div data-testid="delivery-active-card" style={{ background: "white", border: "2px solid #0F3D2E", borderRadius: 16, overflow: "hidden", marginBottom: 18, boxShadow: "0 8px 24px rgba(15,61,46,0.12)" }}>
            <div style={{ padding: "10px 14px", background: "#0F3D2E", color: "white", fontSize: 12.5, fontWeight: 800, display: "flex", justifyContent: "space-between" }}>
              <span>🛵 Active delivery</span>
              <span data-testid="delivery-sharing-status">{agentPos ? "● Sharing live location" : "○ Waiting for GPS"}</span>
            </div>
            <ActiveMap order={active} agentPos={agentPos} />
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{active.product_name}</div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>From {active.shop_name || "shop"} · Qty {active.qty}</div>
              <div style={{ ...S.fraunces, fontSize: 17, fontWeight: 800, color: "#8B1A4A", marginTop: 4 }}>Collect ₹{active.total} · COD</div>

              <div style={{ marginTop: 10, padding: 11, background: "#FAFAF5", border: "1px solid #EAE8DC", borderRadius: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#888", fontWeight: 700 }}>DELIVER TO</div>
                <div style={{ fontSize: 13, marginTop: 3, color: "#333" }}>{active.delivery_address}</div>
                {active.user_mobile && (
                  <a href={`tel:${active.user_mobile}`} data-testid="delivery-call-customer" style={{ display: "inline-block", marginTop: 6, fontSize: 12.5, fontWeight: 700, color: "#0F3D2E" }}>📞 Call {active.user_name || "customer"} · {active.user_mobile}</a>
                )}
              </div>

              {active.status === "confirming_stock" && (
                <button data-testid="delivery-mark-pickedup" disabled={busy} onClick={() => advance(active, "picked_up")}
                  style={{ width: "100%", marginTop: 12, padding: 14, borderRadius: 100, border: "none", background: "#C97B3C", color: "white", fontWeight: 800, fontSize: 14.5, cursor: "pointer" }}>
                  ✅ Mark as Picked Up
                </button>
              )}
              {active.status === "picked_up" && (
                <button data-testid="delivery-mark-delivered" disabled={busy} onClick={() => advance(active, "delivered")}
                  style={{ width: "100%", marginTop: 12, padding: 14, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 800, fontSize: 14.5, cursor: "pointer" }}>
                  🏠 Mark as Delivered
                </button>
              )}
            </div>
          </div>
        )}

        {/* GO ONLINE / OPEN ORDERS */}
        {!active && !online && (
          <div data-testid="delivery-offline-state" style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 46, marginBottom: 10 }}>🛵</div>
            <div style={{ ...S.fraunces, fontSize: 20, fontWeight: 700, color: "#0F3D2E" }}>Become a delivery partner</div>
            <div style={{ fontSize: 13, color: "#666", marginTop: 8, maxWidth: 320, marginInline: "auto", lineHeight: 1.5 }}>
              Go online to see COD orders waiting near you. Accept one, pick it up from the shop, and deliver it to the customer.
            </div>
            <button data-testid="delivery-go-online-btn" onClick={goOnline}
              style={{ marginTop: 20, padding: "14px 30px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 8px 22px rgba(15,61,46,0.3)" }}>
              Go Online
            </button>
          </div>
        )}

        {!active && online && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 700 }}>Open orders near you</div>
              <button data-testid="delivery-go-offline-btn" onClick={() => setOnline(false)} style={{ fontSize: 12, fontWeight: 700, color: "#8B1A4A", background: "none", border: "none", cursor: "pointer" }}>Go offline</button>
            </div>
            {openOrders.length === 0 ? (
              <div data-testid="delivery-no-open-orders" style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
                <div style={{ fontSize: 38, marginBottom: 8 }}>📭</div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>No open orders right now</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>New orders will appear here automatically.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {openOrders.map((o) => {
                  const img = resolveImageSrc(o.product_image);
                  const ok = img && (img.startsWith("http") || img.startsWith("data:"));
                  return (
                    <div key={o.id} data-testid={`delivery-open-order-${o.id}`} style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 14, padding: 12, display: "flex", gap: 12 }}>
                      <div style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 10, overflow: "hidden", background: "#F0EFE8" }}>
                        {ok ? <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🛍️</div>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{o.product_name}</div>
                        <div style={{ fontSize: 11.5, color: "#8B1A4A", fontWeight: 700, marginTop: 2 }}>🏪 {o.shop_name || "Local shop"}{o.distance_km != null ? ` · ${fmtDist(o.distance_km)} away` : ""}</div>
                        <div style={{ fontSize: 11.5, color: "#666", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>📍 {o.delivery_address}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                          <span style={{ ...S.fraunces, fontSize: 15, fontWeight: 800, color: "#0F3D2E" }}>₹{o.total} COD</span>
                          <button data-testid={`delivery-accept-${o.id}`} disabled={busy} onClick={() => accept(o)}
                            style={{ padding: "8px 16px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}>
                            Accept &amp; Deliver
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ActiveMap({ order, agentPos }) {
  const shop = (order.shop_lat != null && order.shop_lng != null) ? [order.shop_lat, order.shop_lng] : null;
  const home = (order.delivery_lat != null && order.delivery_lng != null) ? [order.delivery_lat, order.delivery_lng] : null;
  const me = agentPos ? [agentPos.lat, agentPos.lng] : null;
  const points = [me, shop, home].filter(Boolean);
  if (points.length === 0) {
    return <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF1EC", color: "#888", fontSize: 12 }}>Waiting for location…</div>;
  }
  return (
    <div data-testid="delivery-active-map" style={{ height: 220 }}>
      <MapContainer center={points[0]} zoom={14} style={{ height: "100%", width: "100%" }} attributionControl={false}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" subdomains={["a", "b", "c", "d"]} maxZoom={19} />
        <FitAll points={points} />
        {shop && <Marker position={shop} icon={shopPin} />}
        {home && <Marker position={home} icon={homePin} />}
        {me && <Marker position={me} icon={mePin} />}
        {me && shop && order.status === "confirming_stock" && <Polyline positions={[me, shop]} pathOptions={{ color: "#C97B3C", weight: 4, dashArray: "8 6", opacity: 0.85 }} />}
        {me && home && order.status === "picked_up" && <Polyline positions={[me, home]} pathOptions={{ color: "#0F3D2E", weight: 4, dashArray: "8 6", opacity: 0.85 }} />}
      </MapContainer>
    </div>
  );
}
